import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ProdutoOportunidade } from '../../../../types/orcamento';
import { apiService } from '../../../../services/api';

interface CalculadoraPrecificacaoProps {
    produtos: ProdutoOportunidade[];
    formatCurrency: (value: number | string) => string;
    onUpdateComplete?: () => void;
}

const CalculadoraPrecificacao: React.FC<CalculadoraPrecificacaoProps> = ({
    produtos,
    formatCurrency,
    onUpdateComplete
}) => {
    // Estado do modal
    const [modalAberto, setModalAberto] = useState(false);
    
    // Estados da calculadora
    const [precoVenda, setPrecoVenda] = useState(0);
    const [custoLogistico, setCustoLogistico] = useState(0);
    const [precoCusto, setPrecoCusto] = useState(0);
    const [imposto, setImposto] = useState(0);
    const [custosAdicionais, setCustosAdicionais] = useState(0);
    const [resultado, setResultado] = useState(0);
    const [porcentagemResultado, setPorcentagemResultado] = useState(0);
    const [corFeedback, setCorFeedback] = useState('text-gray-500');
    const [mensagemFeedback, setMensagemFeedback] = useState('');
    const [produtoSelecionado, setProdutoSelecionado] = useState('');
    const [quantidade, setQuantidade] = useState(1);
    const [mostrarTabelaTotais, setMostrarTabelaTotais] = useState(true);
    const [produtoAtual, setProdutoAtual] = useState<ProdutoOportunidade | null>(null);
    
    // Estados para atualização do produto
    const [showConfirmacaoModal, setShowConfirmacaoModal] = useState(false);
    const [atualizandoProduto, setAtualizandoProduto] = useState(false);
    
    // Estados para validação dos campos obrigatórios
    const [impostoInvalido, setImpostoInvalido] = useState(false);
    const [precoCustoInvalido, setPrecoCustoInvalido] = useState(false);
    const [precoVendaInvalido, setPrecoVendaInvalido] = useState(false);
    const [mostrarAlertaCamposObrigatorios, setMostrarAlertaCamposObrigatorios] = useState(false);

    // Funções da calculadora
    const formatarMoeda = (valor: number): string => {
        return formatCurrency(valor);
    };

    const formatarPorcentagem = (valor: number): string => {
        return valor.toFixed(2) + '%';
    };

    // Função para prevenir alteração do valor ao scrollar
    const preventScrollInput = (e: React.WheelEvent<HTMLInputElement>) => {
        e.currentTarget.blur();
    };

    // Função para validar campos obrigatórios
    const validarCamposObrigatorios = (): boolean => {
        let valido = true;
        
        // Validar preço de custo
        if (precoCusto <= 0) {
            setPrecoCustoInvalido(true);
            valido = false;
        } else {
            setPrecoCustoInvalido(false);
        }
        
        // Validar imposto
        if (imposto <= 0) {
            setImpostoInvalido(true);
            valido = false;
        } else {
            setImpostoInvalido(false);
        }
        
        // Validar preço de venda
        if (precoVenda <= 0) {
            setPrecoVendaInvalido(true);
            valido = false;
        } else {
            setPrecoVendaInvalido(false);
        }
        
        // Mostrar/ocultar alerta geral
        if (!valido) {
            setMostrarAlertaCamposObrigatorios(true);
        } else {
            setMostrarAlertaCamposObrigatorios(false);
        }
        
        return valido;
    };

    const calcular = (): void => {
        const impostoDecimal = imposto / 100;
        const valorImposto = precoVenda * impostoDecimal;
        
        const result = precoVenda - custoLogistico - custosAdicionais - precoCusto - valorImposto;
        setResultado(result);
        
        let porcent = 0;
        if (precoVenda > 0) {
            porcent = (result / precoVenda) * 100;
        }
        setPorcentagemResultado(porcent);
        
        if (porcent <= 8) {
            setCorFeedback('text-danger');
            setMensagemFeedback('Atenção: Margem muito baixa!');
        } else if (porcent > 8 && porcent < 15) {
            setCorFeedback('text-warning');
            setMensagemFeedback('Aviso: Margem mediana');
        } else {
            setCorFeedback('text-success');
            setMensagemFeedback('Excelente: Boa margem!');
        }
    };

    // Cálculo do custo total corrigido
    const calcularCustoTotal = (): number => {
        // Garantir que todos os valores são números válidos antes do cálculo
        const custoBase = precoCusto || 0;
        const logistica = custoLogistico || 0;
        const adicionais = custosAdicionais || 0;
        const valorImposto = precoVenda * (imposto / 100) || 0;
        
        return (custoBase + logistica + adicionais + valorImposto) * quantidade;
    };

    const selecionarProduto = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const valor = e.target.value;
        setProdutoSelecionado(valor);
        
        if (valor) {
            const produto = produtos.find(p => p.id.toString() === valor);
            if (produto) {
                setProdutoAtual(produto);
                // Usar valor_orcado como preço de custo se disponível, senão deixar zerado
                setPrecoCusto(produto.valor_orcado !== null ? produto.valor_orcado : 0);
                setQuantidade(produto.quantidade);
                
                // Definir imposto - se não tiver valor, forçar validação
                const impostoValue = produto.percentual_imposto || 0;
                setImposto(impostoValue);
                
                // Definir preço de custo - se não tiver valor, forçar validação
                const precoCustoValue = produto.valor_orcado !== null ? produto.valor_orcado : 0;
                setPrecoCusto(precoCustoValue);
                
                // Validar campos iniciais
                if (impostoValue <= 0) {
                    setImpostoInvalido(true);
                    setMostrarAlertaCamposObrigatorios(true);
                } else {
                    setImpostoInvalido(false);
                }
                
                if (precoCustoValue <= 0) {
                    setPrecoCustoInvalido(true);
                    setMostrarAlertaCamposObrigatorios(true);
                } else {
                    setPrecoCustoInvalido(false);
                }
                // Preencher os novos campos se existirem
                setCustoLogistico(produto.custo_logistico || 0);
                setCustosAdicionais(produto.custos_adicionais || 0);
                
                // Se tiver valor de proposta, usar como valor de venda
                if (produto.valor_proposta !== null) {
                    const precoVendaValue = produto.valor_proposta;
                    setPrecoVenda(precoVendaValue);
                    if (precoVendaValue <= 0) {
                        setPrecoVendaInvalido(true);
                        setMostrarAlertaCamposObrigatorios(true);
                    } else {
                        setPrecoVendaInvalido(false);
                    }
                } else {
                    // Caso contrário, usar valor de referência
                    const precoVendaValue = produto.valor_referencia;
                    setPrecoVenda(precoVendaValue);
                    if (precoVendaValue <= 0) {
                        setPrecoVendaInvalido(true);
                        setMostrarAlertaCamposObrigatorios(true);
                    } else {
                        setPrecoVendaInvalido(false);
                    }
                }
            }
        } else {
            setProdutoAtual(null);
            setPrecoCusto(0);
            setQuantidade(1);
            setPrecoVenda(0);
            setCustoLogistico(0);
            setCustosAdicionais(0);
            setImposto(0);
            setImpostoInvalido(false);
            setPrecoCustoInvalido(false);
            setPrecoVendaInvalido(false);
            setMostrarAlertaCamposObrigatorios(false);
        }
    };

    // Função para lidar com mudança no campo de imposto
    const handleImpostoChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const valor = Number(e.target.value);
        setImposto(valor);
        
        if (valor <= 0) {
            setImpostoInvalido(true);
            setMostrarAlertaCamposObrigatorios(true);
        } else {
            setImpostoInvalido(false);
            // Verificar se outros campos também estão válidos
            if (precoCusto > 0 && precoVenda > 0) {
                setMostrarAlertaCamposObrigatorios(false);
            }
        }
    };

    // Função para lidar com mudança no preço de custo
    const handlePrecoCustoChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const valor = Number(e.target.value);
        setPrecoCusto(valor);
        
        if (valor <= 0) {
            setPrecoCustoInvalido(true);
            setMostrarAlertaCamposObrigatorios(true);
        } else {
            setPrecoCustoInvalido(false);
            // Verificar se outros campos também estão válidos
            if (imposto > 0 && precoVenda > 0) {
                setMostrarAlertaCamposObrigatorios(false);
            }
        }
    };

    // Função para lidar com mudança no preço de venda
    const handlePrecoVendaChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const valor = Number(e.target.value);
        setPrecoVenda(valor);
        
        if (valor <= 0) {
            setPrecoVendaInvalido(true);
            setMostrarAlertaCamposObrigatorios(true);
        } else {
            setPrecoVendaInvalido(false);
            // Verificar se outros campos também estão válidos
            if (imposto > 0 && precoCusto > 0) {
                setMostrarAlertaCamposObrigatorios(false);
            }
        }
    };

    // Calcular sempre que os valores mudarem
    useEffect(() => {
        calcular();
    }, [precoVenda, custoLogistico, precoCusto, imposto, custosAdicionais]);

    const abrirModal = (): void => {
        resetarCampos();
        setModalAberto(true);
    };

    const fecharModal = (): void => {
        setModalAberto(false);
    };

    const resetarCampos = (): void => {
        setPrecoVenda(0);
        setCustoLogistico(0);
        setPrecoCusto(0);
        setImposto(0);
        setCustosAdicionais(0);
        setProdutoSelecionado('');
        setQuantidade(1);
        setProdutoAtual(null);
        setImpostoInvalido(false);
        setPrecoCustoInvalido(false);
        setPrecoVendaInvalido(false);
        setMostrarAlertaCamposObrigatorios(false);
    };

    // Abrir modal de confirmação para atualizar valor proposta
    const confirmarAtualizarValorProposta = (): void => {
        if (!produtoAtual) {
            toast.error("Selecione um produto primeiro!");
            return;
        }
        
        if (precoVenda <= 0) {
            toast.error("O preço de venda deve ser maior que zero!");
            return;
        }
        
        // Validar se os campos obrigatórios foram preenchidos
        if (!validarCamposObrigatorios()) {
            toast.error("Preencha todos os campos obrigatórios: Preço de Custo, Imposto e Preço de Venda!");
            return;
        }
        
        setShowConfirmacaoModal(true);
    };
    
    // Atualizar o valor de proposta do produto
    const atualizarValorProposta = async (): Promise<void> => {
        if (!produtoAtual) return;
        
        // Validar novamente antes de salvar
        if (!validarCamposObrigatorios()) {
            toast.error("Preencha todos os campos obrigatórios!");
            return;
        }
        
        try {
            setAtualizandoProduto(true);
            
            // Atualiza com os novos campos também
            const response = await apiService.updateProdutoOportunidade(produtoAtual.id, {
                valor_proposta: precoVenda,
                custo_logistico: custoLogistico,
                custos_adicionais: custosAdicionais,
                percentual_imposto: imposto
                // A margem_lucro será calculada automaticamente no backend
            });
            
            if (response.success) {
                toast.success(`Valor de proposta atualizado com sucesso para ${produtoAtual.nome_produto}!`);
                
                // Fechar modais
                setShowConfirmacaoModal(false);
                setModalAberto(false);
                
                // IMPORTANTE: Chamar callback de atualização
                if (onUpdateComplete) {
                    onUpdateComplete(); // Isso fará o OrcamentoList recarregar e o OpportunityForm recalcular
                }
            } else {
                toast.error("Erro ao atualizar valor de proposta");
            }
        } catch (error) {
            console.error("Erro ao atualizar valor proposta:", error);
            toast.error("Ocorreu um erro ao atualizar o valor de proposta");
        } finally {
            setAtualizandoProduto(false);
        }
    };

    return (
        <>
            <Button 
                onClick={abrirModal}
                variant="primary"
                className="btn-sm"
            >
                <i className="ti ti-calculator me-1"></i>
                Calculadora de Precificação
            </Button>

            {/* Modal principal da calculadora */}
            <Modal
                show={modalAberto}
                onHide={fecharModal}
                size="lg"
                centered
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Calculadora de Precificação</Modal.Title>
                </Modal.Header>
                
                <Modal.Body>
                    {/* Alerta para campos obrigatórios */}
                    {mostrarAlertaCamposObrigatorios && (
                        <Alert variant="warning" className="mb-3">
                            <i className="ti ti-alert-triangle me-2"></i>
                            <strong>Atenção!</strong> Os campos <strong>Preço de Custo</strong>, <strong>Imposto</strong> e <strong>Preço de Venda</strong> são obrigatórios e devem ser maiores que zero.
                        </Alert>
                    )}

                    {/* Seleção de Produto e Quantidade */}
                    <div className="mb-4 bg-light p-3 rounded">
                        <div className="row">
                            <div className="col-md-8">
                                <div className="form-group">
                                    <label className="form-label">Selecione um Produto</label>
                                    <select
                                        value={produtoSelecionado}
                                        onChange={selecionarProduto}
                                        className="form-select"
                                    >
                                        <option value="">Selecione um produto...</option>
                                        {produtos.map(produto => (
                                            <option key={produto.id} value={produto.id.toString()}>
                                                {produto.nome_produto} - {formatCurrency(produto.valor_referencia)}
                                                {produto.numero_edital ? ` (Nº ${produto.numero_edital})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label className="form-label">Quantidade</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={quantidade}
                                        onChange={(e) => setQuantidade(Math.max(1, parseInt(e.target.value) || 1))}
                                        className="form-control"
                                        onWheel={preventScrollInput}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Campos da Calculadora */}
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <div className="card mb-3">
                                <div className="card-body">
                                    <div className="form-group mb-3">
                                        <label className="form-label">
                                            Preço de Custo <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">R$</span>
                                            <input
                                                type="number"
                                                step="0.0001"
                                                min="0.0001"
                                                value={precoCusto}
                                                onChange={handlePrecoCustoChange}
                                                className={`form-control ${precoCustoInvalido ? 'is-invalid' : ''}`}
                                                onWheel={preventScrollInput}
                                                required
                                            />
                                            {precoCustoInvalido && (
                                                <div className="invalid-feedback">
                                                    O preço de custo é obrigatório
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="form-group">
                                        <label className="form-label">Custo Logístico</label>
                                        <div className="input-group">
                                            <span className="input-group-text">R$</span>
                                            <input
                                                type="number"
                                                step="0.0001"
                                                value={custoLogistico}
                                                onChange={(e) => setCustoLogistico(Number(e.target.value))}
                                                className="form-control"
                                                onWheel={preventScrollInput}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-6">
                            <div className="card mb-3">
                                <div className="card-body">
                                    <div className="form-group mb-3">
                                        <label className="form-label">
                                            Imposto <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                value={imposto}
                                                onChange={handleImpostoChange}
                                                className={`form-control ${impostoInvalido ? 'is-invalid' : ''}`}
                                                onWheel={preventScrollInput}
                                                required
                                            />
                                            <span className="input-group-text">%</span>
                                            {impostoInvalido && (
                                                <div className="invalid-feedback">
                                                    O percentual de imposto é obrigatório
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="form-group">
                                        <label className="form-label">Custos Adicionais</label>
                                        <div className="input-group">
                                            <span className="input-group-text">R$</span>
                                            <input
                                                type="number"
                                                step="0.0001"
                                                value={custosAdicionais}
                                                onChange={(e) => setCustosAdicionais(Number(e.target.value))}
                                                className="form-control"
                                                onWheel={preventScrollInput}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Preço de Venda com botão para atualizar valor proposta */}
                    <div className="card mb-4 border-primary">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="form-label text-center fw-bold mb-0">
                                    Preço de Venda <span className="text-danger">*</span>
                                </label>
                                
                                <Button 
                                    variant="success" 
                                    size="sm"
                                    disabled={!produtoAtual || impostoInvalido || precoCustoInvalido || precoVendaInvalido}
                                    onClick={confirmarAtualizarValorProposta}
                                >
                                    <i className="ti ti-check me-1"></i>
                                    Alterar Valor Proposta
                                </Button>
                            </div>
                            <div className="input-group">
                                <span className="input-group-text">R$</span>
                                <input
                                    type="number"
                                    step="0.0001"
                                    min="0.0001"
                                    value={precoVenda}
                                    onChange={handlePrecoVendaChange}
                                    className={`form-control form-control-lg text-center fw-bold ${precoVendaInvalido ? 'is-invalid' : ''}`}
                                    onWheel={preventScrollInput}
                                    required
                                />
                                {precoVendaInvalido && (
                                    <div className="invalid-feedback">
                                        O preço de venda é obrigatório
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Resultados */}
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 text-center">
                                    <h5 className="fw-bold mb-3">Resultado</h5>
                                    <div className="bg-light rounded p-3 border">
                                        <h3 className="fw-bold text-dark">{formatarMoeda(resultado)}</h3>
                                    </div>
                                </div>
                                
                                <div className="col-md-6 text-center">
                                    <h5 className="fw-bold mb-3">Margem</h5>
                                    <div className="bg-light rounded p-3 border">
                                        <h3 className={`fw-bold ${corFeedback}`}>{formatarPorcentagem(porcentagemResultado)}</h3>
                                        <p className={`mb-0 small ${corFeedback}`}>{mensagemFeedback}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Resumo do Lote */}
                    <div className="card border-primary mb-3">
                        <div className="card-header bg-primary-light d-flex justify-content-between align-items-center">
                            <h5 className="fw-bold mb-0">Resumo do Lote</h5>
                            <Button 
                                variant="link" 
                                className="p-0 text-primary" 
                                onClick={() => setMostrarTabelaTotais(!mostrarTabelaTotais)}
                            >
                                {mostrarTabelaTotais ? (
                                    <><i className="ti ti-chevron-up me-1"></i>Ocultar</>
                                ) : (
                                    <><i className="ti ti-chevron-down me-1"></i>Mostrar</>
                                )}
                            </Button>
                        </div>
                        
                        {mostrarTabelaTotais && (
                            <div className="card-body">
                                <div className="row text-center">
                                    <div className="col">
                                        <div className="mb-2 small text-muted">Quantidade</div>
                                        <div className="h5 fw-bold">{quantidade}</div>
                                        <div className="small text-muted">unidades</div>
                                    </div>
                                    
                                    <div className="col">
                                        <div className="mb-2 small text-muted">Valor Total</div>
                                        <div className="h5 fw-bold">{formatarMoeda(precoVenda * quantidade)}</div>
                                    </div>
                                    
                                    <div className="col">
                                        <div className="mb-2 small text-muted">Custo Total</div>
                                        <div className="h5 fw-bold">{formatarMoeda(calcularCustoTotal())}</div>
                                    </div>
                                    
                                    <div className="col">
                                        <div className="mb-2 small text-muted">Lucro Total</div>
                                        <div className="h5 fw-bold">{formatarMoeda(resultado * quantidade)}</div>
                                    </div>
                                    
                                    <div className="col">
                                        <div className="mb-2 small text-muted">Margem</div>
                                        <div className={`h5 fw-bold ${corFeedback}`}>{formatarPorcentagem(porcentagemResultado)}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                
                <Modal.Footer>
                    <Button variant="secondary" onClick={fecharModal}>
                        Fechar
                    </Button>
                    <Button variant="outline-danger" onClick={resetarCampos}>
                        <i className="ti ti-trash me-1"></i>
                        Limpar Dados
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de confirmação para atualizar valor proposta */}
            <Modal 
                show={showConfirmacaoModal} 
                onHide={() => setShowConfirmacaoModal(false)}
                centered
                backdrop="static"
                size="sm"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Alteração</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Deseja realmente alterar o valor de proposta do produto:</p>
                    <p className="fw-bold text-primary">{produtoAtual?.nome_produto}</p>
                    <p>
                        <span className="d-block">De: {produtoAtual?.valor_proposta ? formatarMoeda(produtoAtual.valor_proposta) : "Não definido"}</span>
                        <span className="d-block fw-bold">Para: {formatarMoeda(precoVenda)}</span>
                    </p>
                    <p className="small text-muted mt-2">
                        Também serão salvos os valores de custos logísticos, adicionais e percentual de imposto para cálculo automático da margem.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmacaoModal(false)} disabled={atualizandoProduto}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={atualizarValorProposta}
                        disabled={atualizandoProduto}
                    >
                        {atualizandoProduto ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Salvando...
                            </>
                        ) : (
                            <>
                                <i className="ti ti-check me-1"></i>
                                Confirmar
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CalculadoraPrecificacao;